import { useRef, useState } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import Button from '../../../../shared/Button';
import CardTabs from '../../../../shared/CardTabs/CardTabs.jsx';
import Text from '../../../../shared/Text';
import { EMAIL, REMINDER, SMS } from '../../utils/constants';
import { SmsPreview } from '../../../../shared/SmsPreview/SmsPreview.jsx';
import { useNotification } from '../../../../shared/Notification/useNotification';
import { downloadEmailPng } from '../../../../lib/helpers/downloadEmailPng';
import { downloadElementPng } from '../../../../lib/helpers/downloadElementPng';
import { campaignMessageFileName } from '../../../../lib/helpers/previewFileName';

export const MessageFormPreview = ({
  demoEmail,
  demoCustomName,
  demoName,
  currentPreview,
  iframeRef,
  iframeSrc,
  setCurrentPreview,
  updateIframe,
  values,
  enableReminder = true,
  currentStep,
}) => {
  const { t } = useTranslation('posts');
  const { showError } = useNotification();
  const [iframeHeight, setIframeHeight] = useState(450);
  const [downloading, setDownloading] = useState(false);
  const phoneRef = useRef(null);
  // Until the iframe loads iframeSrc it still holds the previous (or a
  // blank) document, which is not the email on screen.
  const [loadedSrc, setLoadedSrc] = useState(null);

  const subject =
    currentStep === REMINDER ? values.reminderSubject : values.subject;
  const shownSubject =
    subject || t('reviewModal.messageForm.emptySubjectDefault');
  const from = t('reviewModal.messageForm.fromPreview', {
    demoName: demoCustomName,
    email: demoEmail,
  });

  const tabs = [
    {
      name: EMAIL,
      content: t('reviewModal.messageForm.emailPreview'),
      disabled: !enableReminder,
    },
    {
      name: SMS,
      content: t('reviewModal.messageForm.smsPreview'),
      tooltip: t('reviewModal.messageForm.disabledTextMsgPreview'),
      disabled: !values.sendSms || !enableReminder,
    },
  ];

  const fileName = channel =>
    campaignMessageFileName({
      board: demoName,
      subject: values.subject,
      reminder: currentStep === REMINDER,
      channel,
    });

  const download = () =>
    currentPreview === SMS
      ? downloadElementPng({
          element: phoneRef.current,
          fileName: fileName('text'),
        })
      : downloadEmailPng({
          emailDocument: iframeRef.current.contentDocument,
          subject: shownSubject,
          from,
          fileName: fileName('email'),
        });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await download();
    } catch (error) {
      // A capture that fails on an image can reject with a DOM Event.
      Sentry.captureException(
        error instanceof Error
          ? error
          : new Error('Message preview download failed', { cause: error }),
      );
      showError({ content: t('reviewModal.messageForm.downloadPngFailed') });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <PreviewContainer>
      <TabsRow>
        <div>
          <CardTabs
            tabs={tabs}
            activeTab={currentPreview}
            setActiveTab={setCurrentPreview}
          />
        </div>
        {enableReminder && (
          <Button
            onClick={handleDownload}
            disabled={
              downloading || (currentPreview !== SMS && loadedSrc !== iframeSrc)
            }
            mode="outline"
            variant="black"
            height="40px"
          >
            {downloading
              ? t('reviewModal.messageForm.downloadingPng')
              : t('reviewModal.messageForm.downloadPng')}
          </Button>
        )}
      </TabsRow>
      <Container height={iframeHeight}>
        {!enableReminder && (
          <DisabledText as="div" variant="button" color="black.80">
            No Reminder Set to Preview
          </DisabledText>
        )}
        {enableReminder && currentPreview !== SMS && (
          <>
            <Section>
              <EmailMeta variant="body3" as="h2" mb="3px" mt="0px">
                <strong>{t('reviewModal.messageForm.from')}</strong>
                {from}
              </EmailMeta>
              <EmailMeta variant="body3" as="h2" mb="3px">
                <strong>{t('reviewModal.messageForm.subject')}</strong>
                {shownSubject}
              </EmailMeta>
            </Section>
            <Iframe
              key={iframeSrc}
              id="share_tiles_digest_preview"
              src={iframeSrc}
              width="100%"
              height="100%"
              ref={iframeRef}
              onLoad={() => {
                setLoadedSrc(iframeSrc);
                setIframeHeight(
                  iframeRef.current.contentWindow.document.body.scrollHeight,
                );

                updateIframe();
              }}
            />
          </>
        )}
        {enableReminder && currentPreview === SMS && (
          <SmsPreview
            ref={phoneRef}
            boardName={demoName}
            postId={values.postId}
            subject={values.smsText || subject}
          />
        )}
      </Container>
    </PreviewContainer>
  );
};

const PreviewContainer = styled.div`
  width: 845.4px;
  position: relative;
`;

const TabsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DisabledText = styled(Text)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${themeGet('colors.black.5')};
  border-radius: 8px;
`;

const Section = styled.div`
  text-align: left;
  align-self: flex-start;
`;

const CONTAINER_SPACE_NOT_OCCUPIED_BY_IFRAME = 177;

const Container = styled.div`
  background-color: ${themeGet('colors.white')};
  border: 1px solid ${themeGet('colors.black.10')};
  border-radius: 0px 8px 8px 8px;
  height: ${props => props.height + CONTAINER_SPACE_NOT_OCCUPIED_BY_IFRAME}px;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Iframe = styled.iframe`
  height: 100%;
  padding: 24px 16px;
  display: block;
`;

const EmailMeta = styled(Text)`
  overflow-wrap: anywhere;
`;
